import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import { take } from 'rxjs';

import {
  AttributeService,
  ConfigurableProduct,
  intersection,
  Product,
  ConfigurableOption,
  ProductService,
  TranslateDirective,
  TranslatePipe,
  ConfigService,
} from '@speedy/core';
import { MatOption } from '@angular/material/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { SelectedOptionLabelPipe } from './selected-option-label.pipe';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-configurable',
  templateUrl: './configurable.component.html',
  styleUrls: ['./configurable.component.scss'],
  standalone: true,
  imports: [
    MatFormField,
    MatSelect,
    MatRadioModule,
    MatOption,
    MatError,
    ReactiveFormsModule,
    FormsModule,
    TranslateDirective,
    TranslatePipe,
    SelectedOptionLabelPipe,
    AsyncPipe,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ConfigurableComponent implements OnInit, OnChanges {
  private readonly attributesService = inject(AttributeService);
  private readonly productService = inject(ProductService);
  private readonly configService = inject(ConfigService);

  @Input() list = false;
  @Input() idPrefix = 'default';
  @Input() product!: Product;

  @Output() optionChanged = new EventEmitter<Product>();

  validated = true;
  selectedChildProduct!: Product;
  selectedValues: { [key: string]: any } = {};
  availableProducts: { [key: string]: any } = {};

  get productAttributeSwatchUrl() {
    return this.configService.productAttributeSwatchUrl;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.product?.firstChange) {
      this.initAttributesLabels();
      this.initChildren(this.product);
    }
  }

  ngOnInit() {
    this.initAttributesLabels();
    this.initChildren(this.product);
  }

  changeOption(superAttributeId: string, value: string) {
    const changedAttribute = this.getSuperAttributeById(superAttributeId);
    this.selectedValues[changedAttribute.attribute_id] = value;

    let allOptionsSelected = true;
    for (const superAttribute of this.getConfigurableDataOptions()) {
      if (superAttribute.position > changedAttribute.position) {
        this.selectedValues[superAttribute.attribute_id] = false;
      }

      if (!this.selectedValues[superAttribute.attribute_id]) {
        allOptionsSelected = false;
      }
    }

    if (allOptionsSelected) {
      const product = this.getSimpleProductByAttrValues();

      if (product) {
        this.selectedChildProduct = product;
        this.optionChanged.emit(product);
      }
    }

    this.calculateAvailableProducts(changedAttribute);
    this.validate();
  }

  initAttributesLabels() {
    (this.product as ConfigurableProduct).configurable_data.options.forEach((attr) => {
      if (!attr.label) {
        attr.label = this.attributesService.getAttributeLabel(
          attr.product_attribute_code,
        );
      }
    });
  }

  getSimpleProductByAttrValues() {
    const selectedValuesByAttrCode = {} as any;

    for (const [attributeId, attributeValue] of Object.entries(this.selectedValues)) {
      if (!attributeValue) continue;

      const superAttribute = this.getSuperAttributeByProductAttributeId(attributeId);
      const attributeCode = superAttribute?.product_attribute_code as string;
      selectedValuesByAttrCode[attributeCode] = attributeValue;
    }

    const selectedProduct = this.product?.children?.find((product) =>
      Object.entries(selectedValuesByAttrCode).every(
        ([attrCode, value]) => parseInt(product[attrCode]) === parseInt(value as string),
      ),
    );

    return selectedProduct;
  }

  calculateAvailableProducts(changedSuperAttribute: ConfigurableOption) {
    const nextAttribute = this.getNextAttributeByPosition(changedSuperAttribute);

    const productArrays = Object.entries(this.selectedValues)
      .filter(([attributeId, value]) => value && attributeId)
      .map(([attributeId, value]) => {
        const loopAttribute = this.getSuperAttributeByProductAttributeId(attributeId);
        if (loopAttribute && loopAttribute.position < nextAttribute?.position) {
          const option = loopAttribute.options.find(
            (option) => option.value_index == value,
          );
          return option?.products as any[];
        }
        return [];
      });

    this.availableProducts[nextAttribute?.attribute_id] = intersection(...productArrays);
  }

  getNextAttributeByPosition(changedAttribute: ConfigurableOption) {
    const superAttributes = this.getConfigurableDataOptions();
    let flag = false;
    let resultAttribute = {} as ConfigurableOption;

    for (const superAttribute of superAttributes) {
      if (flag) {
        resultAttribute = superAttribute;
        break;
      }
      if (superAttribute.position === changedAttribute.position) {
        flag = true;
      }
    }

    return resultAttribute;
  }

  isSuperAttributeActive(superAttribute: any) {
    if (parseInt(superAttribute.position) == 0) {
      return true;
    }

    let optionsSelected = true;
    const sortedSuperAttributes = this.getConfigurableDataOptions();
    for (const loopSuperAttribute of sortedSuperAttributes) {
      if (
        parseInt(loopSuperAttribute.position) < parseInt(superAttribute.position) &&
        !this.selectedValues[loopSuperAttribute.attribute_id]
      ) {
        optionsSelected = false;
      }
    }

    return optionsSelected;
  }

  validate() {
    this.validated = true;
    for (const superAttribute of this.product.configurable_data.options) {
      if (!this.selectedValues[superAttribute.attribute_id]) {
        this.validated = false;
      }
    }

    return this.validated;
  }

  getAddToCartOptions() {
    const result = [];
    for (const [attributeId, value] of Object.entries(this.selectedValues)) {
      result.push({
        option_id: attributeId,
        option_value: value,
      });
    }

    return result;
  }

  getSuperAttributeById(superAttributeId: string) {
    return (this.product as ConfigurableProduct).configurable_data.options.find(
      (superAttribute) => superAttribute.product_super_attribute_id === superAttributeId,
    ) as ConfigurableOption;
  }

  getSuperAttributeByProductAttributeId(productAttributeId: string) {
    return (this.product as ConfigurableProduct).configurable_data.options.find(
      (superAttribute) => superAttribute.attribute_id == productAttributeId,
    );
  }

  isOptionAvailable(attribute: ConfigurableOption, option: any) {
    return (
      parseInt(attribute.position) === 0 ||
      this.availableProducts[attribute.attribute_id].some((product: Product) =>
        option.products.includes(product),
      )
    );
  }

  initChildren(parent: Product) {
    this.productService
      .loadProductsChildren([parent])
      .pipe(take(1))
      .subscribe((products) => {
        this.product = products[0];
        // const product = this.getSimpleProductByAttrValues();
        // if (product) {
        //   this.changePrice(product);
        //   this.changeImage(product);
        // }
      });
  }

  getConfigurableDataOptions = () => this.product?.configurable_data?.options;
}
